import React, { useState, useEffect } from "react";
import { Tabs, Typography, message } from "antd";
import { supabase } from "../lib/supabase";
import { trackEvent, trackFeatureUsage, captureError } from "../lib/posthog";
import {
  Component,
  TransportType,
  Rule,
  PRICING_EVENTS,
} from "../types/pricing";
import ComponentsTab from "../components/pricing/ComponentsTab";
import RulesTab from "../components/pricing/RulesTab";
import RuleValuesTab from "../components/pricing/RuleValuesTab";
import TreePricingTab from "../components/pricing/TreePricingTab";
import TreePricingTab2 from "../components/pricing/pricing-tree-v2/TreePricingTab2";
// import SimulationTab from "../components/pricing/simulation/SimulationTab";
import { AdminNavBar } from "../components/AdminNavBar";

const { Title } = Typography;

// Record type for supabase component data
interface ComponentData {
  id: string;
  name: string;
  component_type: string;
  description?: string;
  calculation_type?: string;
  behavior_type?: string; // NEW: Added behavior_type
  // Allow additional unknown properties
  [key: string]: string | number | boolean | null | undefined;
}

const InvoicingAdmin = () => {
  const [activeTab, setActiveTab] = useState("tree2");
  const [components, setComponents] = useState<Component[]>([]);
  const [rules, setRules] = useState<Rule[]>([]);
  const [transportTypes, setTransportTypes] = useState<TransportType[]>([]);
  const [loading, setLoading] = useState(true);

  console.log("[DEBUG] InvoicingAdmin - Initializing component state");

  // Fetch pricing components from Supabase
  const fetchComponents = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("pricing_components")
        .select("*")
        .order("name");

      if (error) {
        throw error;
      }

      if (data) {
        // Map fields to match our Component interface
        const mappedComponents = data.map((item: ComponentData) => ({
          id: item.id,
          name: item.name,
          type: item.component_type,
          cost: 0, // Set default cost (will come from pricing_values)
          is_active: true, // Set default active state
          description: item.description,
          component_type: item.component_type,
          calculation_type: item.calculation_type,
          behavior_type: item.behavior_type || "base", // NEW: Include behavior_type with default
        }));
        setComponents(mappedComponents);
      }
    } catch (error) {
      console.error("Error fetching pricing components:", error);
      message.error("Failed to load pricing components");
      captureError(error, {
        source: "InvoicingAdmin",
        action: "fetchComponents",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch pricing rules from Supabase
  const fetchRules = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("pricing_rules")
        .select(
          `
            *,
          origin_port (*),
          destination_port (*),
          shipment_type (*),
          container_size (*),
          incoterm (*)
        `
        )
        .order("priority", { ascending: false });

      if (error) {
        throw error;
      }

      setRules(data || []);
    } catch (error) {
      console.error("Error fetching rules:", error);
      message.error("Failed to fetch pricing rules");
    } finally {
      setLoading(false);
    }
  };

  // Fetch transport types from Supabase
  const fetchTransportTypes = async () => {
    try {
      const { data, error } = await supabase
        .from("booking_transport_types")
        .select("*")
        .order("name");

      if (error) {
        throw error;
      }

      if (data) {
        setTransportTypes(data);
      }
    } catch (error) {
      console.error("Error fetching transport types:", error);
      message.error("Failed to load transport types");
      captureError(error, {
        source: "InvoicingAdmin",
        action: "fetchTransportTypes",
      });
    }
  };

  useEffect(() => {
    // Track page view
    trackEvent(PRICING_EVENTS.VIEW_PAGE);
    trackFeatureUsage("pricing_admin");

    fetchComponents();
    fetchRules();
    fetchTransportTypes();

    return () => {
      // Cleanup if needed
    };
  }, []);

  const handleTabChange = (key: string) => {
    console.log("[DEBUG] Tab changed to:", key);
    setActiveTab(key);

    // Track tab changes
    trackEvent("pricing_tab_changed", { tab: key });
  };

  console.log("[DEBUG] InvoicingAdmin - About to render component");

  // Try-catch wrapper around rendering to capture any React errors
  try {
    const items = [
      {
        key: "tree2",
        label: "🚀 Pricing Rules V2",
        children: <TreePricingTab2 />,
      },
      {
        key: "tree",
        label: "🌳 Pricing Tree (Legacy)",
        children: <TreePricingTab />,
      },
      {
        key: "components",
        label: "Components",
        children: (
          <ComponentsTab
            components={components}
            loading={loading}
            fetchComponents={fetchComponents}
          />
        ),
      },
      {
        key: "rules",
        label: "Rules",
        children: (
          <RulesTab
            rules={rules}
            transportTypes={transportTypes}
            loading={loading}
            fetchRules={fetchRules}
          />
        ),
      },
      {
        key: "values",
        label: "Rule Values",
        children: <RuleValuesTab rules={rules} />,
      },
      // Simulation tab temporarily hidden - may be used in the future
      // {
      //   key: "simulation",
      //   label: "Simulation",
      //   children: <SimulationTab rules={rules} />,
      // },
    ];

    return (
      <div style={{ padding: 24 }}>
        <AdminNavBar />

        <Title level={2}>Invoice Settings</Title>

        <Tabs activeKey={activeTab} onChange={handleTabChange} items={items} />
      </div>
    );
  } catch (error: unknown) {
    // Catch any rendering errors
    console.error("[DEBUG] InvoicingAdmin - Render error:", error);
    return (
      <div
        style={{
          padding: "2rem",
          background: "#fff1f0",
          border: "1px solid #ffa39e",
        }}
      >
        <h2>Error Rendering Invoice Settings</h2>
        <p>An error occurred while rendering the Invoice Settings page:</p>
        <pre
          style={{ background: "#f5f5f5", padding: "1rem", overflow: "auto" }}
        >
          {error instanceof Error ? error.toString() : String(error)}
          {error instanceof Error ? error.stack : ""}
        </pre>
      </div>
    );
  }
};

export default InvoicingAdmin;
